import { useCallback } from 'react';
import { useAuth } from './useAuth';
import { useQuery } from '@tanstack/react-query';
import { projectsService } from '../services/projects.service';

/**
 * Frontend authorization hook for user-owned resources
 * 
 * NOTE: These are UX-only checks! Real security is enforced by backend.
 * Never trust frontend authorization - always verify on the server.
 * 
 * Purpose:
 * - Prevent navigation to resources user doesn't own (better UX)
 * - Show/hide UI elements based on ownership
 * - Provide early feedback before making invalid API calls
 */

export interface AuthorizationCheck {
  isAuthorized: boolean;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Check if current user owns a project
 * Uses existing projects list from cache when possible to avoid extra API calls
 */
export const useProjectAuthorization = (projectId: number | undefined): AuthorizationCheck => {
  const { isAuthenticated } = useAuth();
  
  const { data: projects, isLoading, error } = useQuery({
    queryKey: ['projects'],
    queryFn: projectsService.getProjects,
    enabled: isAuthenticated && !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes - reuse cached data
  });

  // If not authenticated or no projectId, not authorized
  if (!isAuthenticated || !projectId) {
    return { isAuthorized: false, isLoading: false, error: null };
  }

  // While loading, assume not authorized (optimistic security)
  if (isLoading) {
    return { isAuthorized: false, isLoading: true, error: null };
  }

  // If error fetching projects, not authorized
  if (error) {
    return { isAuthorized: false, isLoading: false, error: error as Error };
  }

  // Check if project exists in user's projects list
  const isAuthorized = projects?.some(p => p.id === projectId) ?? false;

  return { isAuthorized, isLoading: false, error: null };
};

/**
 * Check if current user owns an episode (via project ownership)
 * Episodes belong to projects, so we check if the episode's project is owned by user
 */
export const useEpisodeAuthorization = (
  projectId: number | undefined,
  episodeId: number | undefined
): AuthorizationCheck => {
  const { isAuthenticated } = useAuth();
  const projectAuth = useProjectAuthorization(projectId);

  // If not authenticated or missing IDs, not authorized
  if (!isAuthenticated || !projectId || !episodeId) {
    return { isAuthorized: false, isLoading: false, error: null };
  }

  // Episode authorization depends on project authorization
  // If user owns the project, they own all episodes in it
  return projectAuth;
};

/**
 * Generic authorization check - can be extended for other resource types
 */
export const useResourceAuthorization = (
  resourceType: 'project' | 'episode' | 'vocabulary' | 'segment',
  resourceId: number | undefined,
  additionalParams?: { projectId?: number; episodeId?: number }
): AuthorizationCheck => {
  const { isAuthenticated } = useAuth();

  // Route to appropriate authorization check based on resource type
  switch (resourceType) {
    case 'project':
      return useProjectAuthorization(resourceId);
    
    case 'episode':
      return useEpisodeAuthorization(additionalParams?.projectId, resourceId);
    
    case 'vocabulary':
      // Vocabulary is user-scoped, so if authenticated, user owns their own vocabulary
      return {
        isAuthorized: isAuthenticated,
        isLoading: false,
        error: null,
      };
    
    case 'segment':
      // Segments belong to projects, check project ownership
      return useProjectAuthorization(additionalParams?.projectId);
    
    default:
      return { isAuthorized: false, isLoading: false, error: null };
  }
};

/**
 * Helper hook to check multiple resource authorizations at once
 * Useful for pages that display multiple resources
 */
export const useBulkAuthorization = (
  checks: Array<{
    resourceType: 'project' | 'episode' | 'vocabulary' | 'segment';
    resourceId: number | undefined;
    additionalParams?: { projectId?: number; episodeId?: number };
  }>
): AuthorizationCheck => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return { isAuthorized: false, isLoading: false, error: null };
  }

  // Run all checks - all must pass for authorization
  const results = checks.map(check =>
    useResourceAuthorization(check.resourceType, check.resourceId, check.additionalParams)
  );

  const isLoading = results.some(r => r.isLoading);
  const error = results.find(r => r.error)?.error || null;
  const isAuthorized = results.every(r => r.isAuthorized);

  return { isAuthorized, isLoading, error };
};

/**
 * Callback-based authorization check for conditional logic
 * Use when you need to check authorization before an action (like delete)
 */
export const useAuthorizationCallback = () => {
  const { isAuthenticated } = useAuth();

  const checkProjectOwnership = useCallback(
    async (projectId: number): Promise<boolean> => {
      if (!isAuthenticated) return false;

      try {
        const projects = await projectsService.getProjects();
        return projects.some(p => p.id === projectId);
      } catch (error) {
        console.error('Authorization check failed:', error);
        return false;
      }
    },
    [isAuthenticated]
  );

  const checkEpisodeOwnership = useCallback(
    async (projectId: number): Promise<boolean> => {
      // Episode ownership is determined by project ownership
      return checkProjectOwnership(projectId);
    },
    [checkProjectOwnership]
  );

  return {
    checkProjectOwnership,
    checkEpisodeOwnership,
  };
};
